import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { EndPoints } from "../../Helpers/Constants/EndPoints";
import Loader from "../Common/Loader";
import API from "../../Helpers/Constants/EndPoints/Api";
import Axios from "../../Helpers/Universal/Axios/Axios";
import { CreatedAt } from "../../Helpers/Universal/Function/common";
import { SuperAdminSchema } from "../../Helpers/Constants/Schemas";
import { UPDATE } from "../../Helpers/Constants/Default";
import { State } from "country-state-city";
import Switch from "react-switch";

const SuperAdminView = () => {
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const paginate = useSelector((val) => val.paginate);

  const [data, setData] = useState({});
  const [visible, setVisible] = useState({ loader: false });
  const [permissions, setPermissions] = useState([]);
  const [filter] = useState({
    limit: 10,
    offset: paginate?.subAdmin || 0,
  });

  const [statesAndUTs, setStatesAndUTs] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [stateLabel, setStateLabel] = useState("");
  const [statesCode, setStatesCode] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [isTypeEditable, setIsTypeEditable] = useState(false);

  const typeToRoute = {
    SUPERADMIN: {
      create: EndPoints.ADD_SUPER_ADMIN,
      update: EndPoints.EDIT_SUPER_ADMIN,
    },
    ADMIN: {
      create: EndPoints.ADD_ADMIN,
      update: EndPoints.EDIT_ADMIN,
    },
    SUBADMIN: {
      create: EndPoints.ADD_SUB_ADMIN,
      update: EndPoints.EDIT_SUB_ADMIN,
    },
    MANAGER: {
      create: EndPoints.ADD_MANAGER,
      update: EndPoints.EDIT_MANAGER,
    },
    SUPERVISOR: {
      create: EndPoints.ADD_SUPERVISOR,
      update: EndPoints.EDIT_SUPERVISOR,
    },
    AGENT: {
      create: EndPoints.ADD_AGENT,
      update: EndPoints.EDIT_AGENT,
    },
  };

  const getData = async () => {
    try {
      setVisible({ loader: true });
      const { status, data } = await Axios.get(
        API.SUPERADMIN.DETAILS + "/" + locationState
      );
      if (status) {
        setData(data);
        setPermissions(data.roleId?.permissions || []);
        
        // Set initial state values if data exists
        if (data.address) {
          setStateLabel(data.address.state);
          setStatesCode(data.address.stateCode);
          getCities(data.address.stateCode);
        }
      }
    } catch (err) {
      console.log("ERROR==>", err);
    } finally {
      setVisible({ loader: false });
    }
  };

  const getStates = async () => {
    try {
      setVisible((prev) => ({ ...prev, loader: true }));
      const { status, data } = await Axios.get(API.LOCATION.STATES);
      if (status) {
        setStatesAndUTs(data || []);
      }
    } catch (err) {
      console.log("ERROR==>", err);
    } finally {
      setVisible((prev) => ({ ...prev, loader: false }));
    }
  };

  const getCities = async (stateCode) => {
    if (!stateCode) return;

    try {
      setVisible((prev) => ({ ...prev, loader: true }));
      const { status, data } = await Axios.get(
        `${API.LOCATION.CITY}?stateCode=${stateCode}`
      );
      if (status) {
        setCityOptions(data || []);
      }
    } catch (err) {
      console.log("ERROR==>", err);
    } finally {
      setVisible((prev) => ({ ...prev, loader: false }));
    }
  };

  useEffect(() => {
    if (locationState) {
      getData();
      getStates();
    } else {
      navigate(EndPoints.SUPER_ADMIN);
    }
  }, [locationState]);

  const handlePermissionChange = (index, field, value) => {
    if (!isEditMode) return;

    const updatedPermissions = [...permissions];
    updatedPermissions[index][field] = value;
    if (field === "all") {
      updatedPermissions[index].view = value;
      updatedPermissions[index].add = value;
      updatedPermissions[index].edit = value;
      updatedPermissions[index].delete = value;
    }
    setPermissions(updatedPermissions);
  };

  const updateBlockStatus = async () => {
    try {
      const { status } = await Axios.patch(API.SUPERADMIN.UPDATE, {
        subAdminId: data._id,
        isBlocked: !data.isBlocked,
      });
      if (status) {
        setData((prev) => ({
          ...prev,
          isBlocked: !prev.isBlocked,
        }));
      }
    } catch (err) {
      console.log("ERROR==>", err);
    }
  };

  const handleUpdateProfile = async (values) => {
    try {
      const addressObject = {};

      if (stateLabel) {
        addressObject.state = stateLabel;
      }
      if (statesCode) {
        addressObject.stateCode = statesCode;
      }
      if (values.city) {
        addressObject.city = values.city;
      }

      if (Object.keys(addressObject).length > 0) {
        addressObject.country = "India";
      }

      const params = {
        subAdminId: data._id,
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password || undefined, // Only send if provided
        isBlocked: data.isBlocked,
        roleId: data.roleId._id,
        permissions: permissions,
      };

      if (Object.keys(addressObject).length > 0) {
        params.address = addressObject;
      }

      const { status } = await Axios.patch(API.SUPERADMIN.UPDATE, params);
      if (status) {
        // Refresh data after update
        getData();
        setIsEditMode(false);
        setIsTypeEditable(false);
        alert("Profile updated successfully!");
      }
    } catch (err) {
      console.log("ERROR==>", err);
      alert("Error updating profile");
    }
  };

  const validateUserName = (value) => {
    const regex = /^[a-zA-Z0-9.]*$/;
    if (!regex.test(value)) {
      return "User ID must be alphanumeric and may include periods.";
    }
    return undefined;
  };

  return (
    <>
      {visible.loader && <Loader />}
      <div className="profile-view">
        <div className="inner-agent">
          <div className="d-flex justify-content-between align-items-center mb-4 p-4">
            <div className="d-flex align-items-center">
              {/* <span className="me-2">Edit Mode</span> */}
              <Switch
                onChange={() => setIsEditMode(!isEditMode)}
                checked={isEditMode}
                onColor="#3e4396"
                offColor="#141b2d"
              />
            </div>
            <div className="d-flex align-items-center">
              <span className="me-2">Block User</span>
              <Switch
                onChange={updateBlockStatus}
                checked={data.isBlocked || false}
                onColor="#3e4396"
                offColor="#141b2d"
              />
            </div>
          </div>
          
          <div className="row mx-0 p-3">
            <div className="col-12">
              <Formik
                initialValues={{
                  type: data.type || "SUPERADMIN",
                  firstName: data.firstName || "",
                  lastName: data.lastName || "",
                  userName: data.userName || "",
                  password: data.dcryptedPass||"",
                  state: data.address?.state || "",
                  city: data.address?.city || "",
                }}
                validationSchema={SuperAdminSchema}
                onSubmit={handleUpdateProfile}
                enableReinitialize
              >
                {({ isSubmitting, errors, touched, setFieldValue, values }) => (
                  <Form>
                    <div className="row mb-4">
                      <div className="col-md-4 mb-3">
                        <label>Type</label>
                        <Field
                          className="form-control"
                          type="text"
                          name="type"
                          disabled={true}
                        />
                      </div>

                      <div className="col-md-4 mb-3">
                        <label>First Name</label>
                        <Field
                          className="form-control"
                          type="c"
                          name="firstName"
                          disabled={!isEditMode}
                        />
                        {errors.firstName && touched.firstName ? (
                          <div className="error">{errors.firstName}</div>
                        ) : null}
                      </div>

                      <div className="col-md-4 mb-3">
                        <label>Last Name</label>
                        <Field
                          className="form-control"
                          type="text"
                          name="lastName"
                          disabled={!isEditMode}
                        />
                        {errors.lastName && touched.lastName ? (
                          <div className="error">{errors.lastName}</div>
                        ) : null}
                      </div>

                      <div className="col-md-4 mb-3">
                        <label>User ID</label>
                        <Field
                          className="form-control"
                          type="text"
                          name="userName"
                          disabled={true}
                          validate={validateUserName}
                        />
                        {errors.userName && touched.userName ? (
                          <div className="error">{errors.userName}</div>
                        ) : null}
                      </div>

                      <div className="col-md-4 mb-3">
                        <label>Password</label>
                        <Field
                          className="form-control"
                          type="text"
                          name="password"
                          disabled={!isEditMode}
                        />
                        {errors.password && touched.password ? (
                          <div className="error">{errors.password}</div>
                        ) : null}
                      </div>

                      <div className="col-md-4 mb-3">
                        <label>Status</label>
                        <div
                          className="form-control"
                          style={{ background: "#1f2a40", opacity: 1 }}
                        >
                          {data.isBlocked ? "Blocked" : "Active"}
                        </div>
                      </div>

                      <div className="col-md-4 mb-3">
                        <label>State</label>
                        <Field
                          as="select"
                          className="form-control bg-dark text-light"
                          name="state"
                          disabled={!isEditMode}
                          onChange={(e) => {
                            const selectedStateCode = e.target.value;
                            const selectedState = statesAndUTs.find(
                              (item) => item.value === selectedStateCode
                            );

                            if (selectedState) {
                              setStateLabel(selectedState.label);
                              setStatesCode(selectedState.value);
                              setFieldValue("state", selectedState.label);
                              getCities(selectedState.value);
                            }
                          }}
                          value={statesCode}
                        >
                          <option value={data.address?.stateCode || ""}>
                            {data.address?.state || "Select state"}
                          </option>
                          {statesAndUTs.map((state, index) => (
                            <option key={index} value={state.value}>
                              {state.label}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="state"
                          component="div"
                          className="error"
                        />
                      </div>

                      <div className="col-md-4 mb-3">
                        <label>City</label>
                        <Field
                          as="select"
                          name="city"
                          className="form-control bg-dark text-light"
                          disabled={!isEditMode}
                        >
                          <option value={data.address?.city || ""}>
                            {data.address?.city || "Select city"}
                          </option>
                          {cityOptions.map((city, index) => (
                            <option key={index} value={city.label}>
                              {city.label}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="city"
                          component="div"
                          className="error"
                        />
                      </div>

                      <div className="col-md-4 mb-3">
                        <label>Created Date</label>
                        <div
                          className="form-control"
                          style={{ background: "#1f2a40", opacity: 1 }}
                        >
                          {CreatedAt(data.createdAt)}
                        </div>
                      </div>

                      {isEditMode && (
                        <div className="col-md-12 mb-3">
                          <button
                            className="btn bg-gradient-dark mt-3 d-block btn-md mb-1"
                            type="submit"
                            disabled={isSubmitting}
                          >
                            {UPDATE} Profile
                          </button>
                        </div>
                      )}
                    </div>
                  </Form>
                )}
              </Formik>

              {/* Permissions Section */}
              <div className="bg-white p-4 border border-secondary rounded">
                <table className="table table-bordered text-center">
                  <thead className="bg-light text-dark">
                    <tr>
                      <th scope="col">Menu Name</th>
                      <th scope="col">All</th>
                      <th scope="col">View</th>
                      <th scope="col">Add</th>
                      <th scope="col">Edit</th>
                      <th scope="col">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {permissions.map((access, index) => (
                      <tr key={index}>
                        <td>{access.name}</td>
                        <td>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={access.all || false}
                            disabled={!isEditMode}
                            onChange={(e) =>
                              handlePermissionChange(
                                index,
                                "all",
                                e.target.checked
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={access.view || false}
                            disabled={!isEditMode}
                            onChange={(e) =>
                              handlePermissionChange(
                                index,
                                "view",
                                e.target.checked
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={access.add || false}
                            disabled={!isEditMode}
                            onChange={(e) =>
                              handlePermissionChange(
                                index,
                                "add",
                                e.target.checked
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={access.edit || false}
                            disabled={!isEditMode}
                            onChange={(e) =>
                              handlePermissionChange(
                                index,
                                "edit",
                                e.target.checked
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={access.delete || false}
                            disabled={!isEditMode}
                            onChange={(e) =>
                              handlePermissionChange(
                                index,
                                "delete",
                                e.target.checked
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {isEditMode && (
                  <div className="d-flex justify-content-end mt-3">
                    <button
                      type="button"
                      className="btn bg-gradient-dark btn-md"
                      onClick={() => {
                        // This will be saved when the main form is submitted
                        alert("Permissions will be saved with profile updates");
                      }}
                    >
                      Save Permissions
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SuperAdminView;